export const CANDIDATE_MODE_VALUES = "values";
export const CANDIDATE_MODE_PERCENT = "percent";
export const MUNICIPAL_CANDIDATE_MODEL = "municipal_candidate_votes";
export const PREFERENCE_VOTES_MODEL = "preference_votes";

function normalizeId(value) {
  return String(value ?? "").trim();
}

function toFiniteNumber(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function toFiniteInt(value) {
  const n = Number(value);
  return Number.isInteger(n) ? n : null;
}

function compareOfficialOrder(a, b) {
  const orderA = toFiniteNumber(a?.candidate_order);
  const orderB = toFiniteNumber(b?.candidate_order);
  if (orderA !== null && orderB !== null && orderA !== orderB) return orderA - orderB;
  if (orderA !== null && orderB === null) return -1;
  if (orderA === null && orderB !== null) return 1;
  return normalizeId(a?.candidate_name).localeCompare(normalizeId(b?.candidate_name), "cs-CZ", {
    numeric: true,
    sensitivity: "base",
  });
}

export function isMunicipalCandidateArtifact(artifact) {
  return normalizeId(artifact?.model) === MUNICIPAL_CANDIDATE_MODEL;
}

export function sortCandidatesByOfficialOrder(candidates) {
  return [...(Array.isArray(candidates) ? candidates : [])].sort(compareOfficialOrder);
}

export function getSelectableCandidatesForList(artifact, list) {
  const ordered = sortCandidatesByOfficialOrder(list?.candidates);
  if (!isMunicipalCandidateArtifact(artifact)) return ordered;
  const limit = Math.ceil(ordered.length / 2);
  return ordered.slice(0, limit);
}

export function buildCandidateArtifactIndexes(artifact) {
  const listsById = new Map();
  const candidateById = new Map();
  const listIdByCandidateId = new Map();
  const candidateValuesByPrecinct = new Map();

  for (const list of Array.isArray(artifact?.lists) ? artifact.lists : []) {
    const listId = normalizeId(list?.list_id);
    if (!listId) continue;
    listsById.set(listId, list);
    for (const candidate of Array.isArray(list?.candidates) ? list.candidates : []) {
      const candidateId = normalizeId(candidate?.candidate_id);
      if (!candidateId) continue;
      candidateById.set(candidateId, candidate);
      listIdByCandidateId.set(candidateId, listId);
    }
  }

  for (const precinct of Array.isArray(artifact?.precincts) ? artifact.precincts : []) {
    const precinctId = normalizeId(precinct?.okrsek);
    if (!precinctId) continue;
    const values = new Map();
    for (const row of Array.isArray(precinct?.values) ? precinct.values : []) {
      const candidateId = normalizeId(row?.candidate_id);
      const absVotes = toFiniteNumber(row?.abs_votes);
      if (candidateId && absVotes !== null) values.set(candidateId, absVotes);
    }
    candidateValuesByPrecinct.set(precinctId, values);
  }

  return {
    listsById,
    candidateById,
    listIdByCandidateId,
    candidateValuesByPrecinct,
  };
}

export function buildRowsByPrecinct(rows) {
  const rowsByPrecinct = new Map();
  for (const row of Array.isArray(rows) ? rows : []) {
    const precinctId = normalizeId(row?.precinctId);
    if (precinctId) rowsByPrecinct.set(precinctId, row);
  }
  return rowsByPrecinct;
}

export function buildResultsByPrecinct(results) {
  const next = new Map();
  for (const row of Array.isArray(results) ? results : []) {
    const precinctId = normalizeId(row?.okrsek);
    if (precinctId) next.set(precinctId, Array.isArray(row?.results) ? row.results : []);
  }
  return next;
}

export function buildStatsByPrecinct(stats) {
  const next = new Map();
  for (const row of Array.isArray(stats) ? stats : []) {
    const precinctId = normalizeId(row?.okrsek);
    if (precinctId) next.set(precinctId, row);
  }
  return next;
}

export function getSubmittedEnvelopes(statsRow) {
  return toFiniteNumber(
    statsRow?.ODEVZ_OBAL ??
      statsRow?.odevz_obal ??
      statsRow?.ODEVZ_OBALEK ??
      statsRow?.odevz_obalek ??
      statsRow?.submitted_envelopes
  );
}

function getPartyVotes(resultRow) {
  return toFiniteNumber(resultRow?.votes ?? resultRow?.hlasy ?? resultRow?.value);
}

function sameNormalizedValue(a, b) {
  const valueA = normalizeId(a);
  const valueB = normalizeId(b);
  return Boolean(valueA && valueB && valueA === valueB);
}

export function findPartyResultForList(resultsForPrecinct, list) {
  const rows = Array.isArray(resultsForPrecinct) ? resultsForPrecinct : [];
  const listPartyOrder = toFiniteInt(list?.party_order);
  const listPartyCode = normalizeId(list?.party_code);
  const listPartyName = normalizeId(list?.party_name);

  if (listPartyOrder !== null) {
    const byOrder = rows.find((row) => toFiniteInt(row?.party_order) === listPartyOrder);
    if (byOrder) return byOrder;
  }

  if (listPartyCode) {
    const byCode = rows.find((row) => sameNormalizedValue(row?.party_code, listPartyCode));
    if (byCode) return byCode;
  }

  if (listPartyName) {
    return rows.find((row) => sameNormalizedValue(row?.party, listPartyName)) || null;
  }

  return null;
}

export function getFeaturePrecinctId(feature) {
  return normalizeId(feature?.properties?.okrsek || feature?.properties?.district_code || feature?.properties?.okrsek_code);
}

function getPrecinctSourceIds(artifact, features) {
  const ids = [];
  const seen = new Set();
  const add = (value) => {
    const id = normalizeId(value);
    if (!id || seen.has(id)) return;
    seen.add(id);
    ids.push(id);
  };

  for (const feature of Array.isArray(features) ? features : []) add(getFeaturePrecinctId(feature));
  if (ids.length > 0) return ids;

  for (const precinct of Array.isArray(artifact?.precincts) ? artifact.precincts : []) add(precinct?.okrsek);
  return ids;
}

function findKvReferenceCandidate(valueMap, referenceCandidates) {
  let reference = null;
  for (const candidate of Array.isArray(referenceCandidates) ? referenceCandidates : []) {
    const candidateId = normalizeId(candidate?.candidate_id);
    const absVotes = valueMap?.get(candidateId);
    if (!Number.isFinite(absVotes)) continue;
    if (
      !reference ||
      absVotes < reference.absVotes ||
      (absVotes === reference.absVotes && compareOfficialOrder(candidate, reference.candidate) < 0)
    ) {
      reference = {
        candidate,
        candidateId,
        absVotes,
      };
    }
  }
  return reference;
}

export function buildCandidateMetricRows({
  artifact,
  selectedListId,
  selectedCandidateId,
  features,
  results,
  stats,
} = {}) {
  if (!artifact || !selectedListId || !selectedCandidateId) return [];

  const indexes = buildCandidateArtifactIndexes(artifact);
  const selectedList = indexes.listsById.get(normalizeId(selectedListId));
  const selectedCandidate = indexes.candidateById.get(normalizeId(selectedCandidateId));
  if (!selectedList || !selectedCandidate) return [];

  const isMunicipal = isMunicipalCandidateArtifact(artifact);
  const referenceCandidates = isMunicipal ? getSelectableCandidatesForList(artifact, selectedList) : [];
  const resultsByPrecinct = buildResultsByPrecinct(results);
  const statsByPrecinct = buildStatsByPrecinct(stats);
  const precinctIds = getPrecinctSourceIds(artifact, features);

  return precinctIds.map((precinctId) => {
    const values = indexes.candidateValuesByPrecinct.get(precinctId) || new Map();
    const candidateAbsVotes = values.get(normalizeId(selectedCandidateId));
    const statsRow = statsByPrecinct.get(precinctId) || null;
    const submittedEnvelopes = getSubmittedEnvelopes(statsRow);
    let absoluteValue = Number.isFinite(candidateAbsVotes) ? candidateAbsVotes : null;
    let percentValue = null;
    let partyVotes = null;
    let referenceCandidate = null;
    let referenceVotes = null;

    if (isMunicipal) {
      const reference = findKvReferenceCandidate(values, referenceCandidates);
      if (reference && Number.isFinite(candidateAbsVotes)) {
        referenceCandidate = reference.candidate;
        referenceVotes = reference.absVotes;
        absoluteValue = candidateAbsVotes - reference.absVotes;
        const denominator = submittedEnvelopes === null ? null : submittedEnvelopes - reference.absVotes;
        if (Number.isFinite(denominator) && denominator > 0) {
          percentValue = (absoluteValue / denominator) * 100;
        }
      } else {
        absoluteValue = null;
      }
    } else {
      const partyResult = findPartyResultForList(resultsByPrecinct.get(precinctId), selectedList);
      partyVotes = getPartyVotes(partyResult);
      if (Number.isFinite(candidateAbsVotes) && Number.isFinite(partyVotes) && partyVotes > 0) {
        percentValue = (candidateAbsVotes / partyVotes) * 100;
      }
    }

    return {
      precinctId,
      candidateId: normalizeId(selectedCandidateId),
      candidateName: normalizeId(selectedCandidate?.candidate_name) || normalizeId(selectedCandidateId),
      selectedListId: normalizeId(selectedListId),
      listName: normalizeId(selectedList?.party_name),
      absoluteValue,
      percentValue,
      candidateAbsVotes: Number.isFinite(candidateAbsVotes) ? candidateAbsVotes : null,
      partyVotes,
      submittedEnvelopes,
      referenceCandidate,
      referenceCandidateName: normalizeId(referenceCandidate?.candidate_name),
      referenceVotes,
      precinctSize: submittedEnvelopes,
    };
  });
}

export function sortCandidateRows(rows, sortKey, model, direction = "desc") {
  const key = normalizeId(sortKey) || "precinct";
  const isMunicipal = normalizeId(model) === MUNICIPAL_CANDIDATE_MODEL;
  const multiplier = normalizeId(direction) === "asc" ? 1 : -1;
  const getMetric = (row) => {
    if (key === "precinct_size") return row?.precinctSize;
    if (key === "percent") return row?.percentValue;
    if (key === "absolute") return isMunicipal ? row?.absoluteValue : row?.candidateAbsVotes;
    if (key === "difference") return row?.differenceValue;
    return null;
  };

  return [...(Array.isArray(rows) ? rows : [])].sort((a, b) => {
    if (key === "precinct") {
      const byPrecinct = normalizeId(a?.precinctId).localeCompare(normalizeId(b?.precinctId), "cs-CZ", {
        numeric: true,
        sensitivity: "base",
      });
      return multiplier * byPrecinct;
    }
    const valueA = toFiniteNumber(getMetric(a));
    const valueB = toFiniteNumber(getMetric(b));
    if (valueA !== null && valueB !== null && valueA !== valueB) return multiplier * (valueA - valueB);
    if (valueA !== null && valueB === null) return -1;
    if (valueA === null && valueB !== null) return 1;
    return normalizeId(a?.precinctId).localeCompare(normalizeId(b?.precinctId), "cs-CZ", {
      numeric: true,
      sensitivity: "base",
    });
  });
}
